import { defineStore } from 'pinia'
import type { ThemeColorsInterface } from '~~/server/types/settings'

export interface PreviewPalette {
    id: number
    name: string
    colors: ThemeColorsInterface | null
}

export const usePalettePreviewStore = defineStore('palette-preview', {
    state: () => ({
        list: null as PreviewPalette[] | null,
        selectedId: null as number | null,
        loading: false,
        error: false,
    }),
    actions: {
        async initialize() {
            const cookie = useCookie<string | null>('design_palette_preview', { path: '/', sameSite: 'lax' })
            const settingsStore = useSettingsStore()
            const selectedId = Number(cookie.value)
            this.loading = true
            this.error = false

            try {
                const { $api } = useNuxtApp()
                this.list = await $api<PreviewPalette[]>('design-templates')

                if (Number.isInteger(selectedId) && selectedId > 0) {
                    if (this.list.some(palette => palette.id === selectedId)) {
                        await settingsStore.loadPreview(selectedId)
                        this.selectedId = selectedId
                    } else {
                        cookie.value = 'enabled'
                    }
                }
            } catch {
                this.error = true
            } finally {
                this.loading = false
            }
        },

        async select(id: number | null) {
            if (this.loading) return
            const cookie = useCookie<string | null>('design_palette_preview', { path: '/', sameSite: 'lax' })
            const settingsStore = useSettingsStore()
            this.loading = true
            this.error = false

            try {
                await settingsStore.loadPreview(id)
                this.selectedId = id
                cookie.value = id === null ? 'enabled' : String(id)
            } catch {
                this.error = true
            } finally {
                this.loading = false
            }
        },

        async disable() {
            if (this.loading) return false
            const cookie = useCookie<string | null>('design_palette_preview', { path: '/', sameSite: 'lax' })
            const settingsStore = useSettingsStore()
            this.loading = true
            this.error = false

            try {
                await settingsStore.loadPreview(null)
                cookie.value = null
                this.selectedId = null
                return true
            } catch {
                this.error = true
                return false
            } finally {
                this.loading = false
            }
        },
    },
})
